import type { ReactNode } from "react";
import { Button } from "../../components/index.js";
import {
  emptySkuRow,
  type SkuFormRow,
  type SkuRowErrors
} from "../../lib/cadastro-form.js";

/**
 * Editor da tabela de SKUs cadastrados (Design.md §8.2/§8.3): SKU · ASIN ·
 * Descrição · Média de Pedido 30 dias. Linhas adicionáveis/removíveis, erros por
 * célula em laranja. Componente controlado (estado mora no formulário pai).
 */
export interface SkuTableEditorProps {
  rows: SkuFormRow[];
  errors: SkuRowErrors[];
  onChange: (rows: SkuFormRow[]) => void;
  disabled?: boolean;
}

const HEAD_STYLE = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  textAlign: "left",
  padding: "0.4rem 0.5rem"
} as const;

export function SkuTableEditor({
  rows,
  errors,
  onChange,
  disabled = false
}: SkuTableEditorProps): ReactNode {
  function updateRow(index: number, patch: Partial<SkuFormRow>): void {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index: number): void {
    onChange(rows.filter((_, i) => i !== index));
  }

  function addRow(): void {
    onChange([...rows, emptySkuRow()]);
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.4rem"
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700 }}>SKUs cadastrados</span>
        <Button variant="secondary" onClick={addRow} disabled={disabled}>
          + Adicionar SKU
        </Button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            <th style={HEAD_STYLE}>SKU</th>
            <th style={HEAD_STYLE}>ASIN</th>
            <th style={HEAD_STYLE}>Descrição</th>
            <th style={{ ...HEAD_STYLE, textAlign: "right" }}>Média 30d</th>
            <th style={{ ...HEAD_STYLE, width: 40 }} aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={5}
                style={{
                  padding: "0.7rem 0.5rem",
                  color: "var(--text-muted)",
                  textAlign: "center"
                }}
              >
                Nenhum SKU. Use "Adicionar SKU".
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const rowErrors = errors[index] ?? {};
              return (
                <tr key={index}>
                  <td style={{ padding: "0.3rem 0.5rem", verticalAlign: "top" }}>
                    <input
                      aria-label={`SKU da linha ${index + 1}`}
                      value={row.sku}
                      disabled={disabled}
                      onChange={(e) => updateRow(index, { sku: e.target.value })}
                      style={{ width: "100%" }}
                    />
                    <CellError message={rowErrors.sku} />
                  </td>
                  <td style={{ padding: "0.3rem 0.5rem", verticalAlign: "top" }}>
                    <input
                      aria-label={`ASIN da linha ${index + 1}`}
                      value={row.asin}
                      disabled={disabled}
                      onChange={(e) => updateRow(index, { asin: e.target.value })}
                      style={{ width: "100%" }}
                    />
                    <CellError message={rowErrors.asin} />
                  </td>
                  <td style={{ padding: "0.3rem 0.5rem", verticalAlign: "top" }}>
                    <input
                      aria-label={`Descrição da linha ${index + 1}`}
                      value={row.description}
                      disabled={disabled}
                      onChange={(e) =>
                        updateRow(index, { description: e.target.value })
                      }
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={{ padding: "0.3rem 0.5rem", verticalAlign: "top" }}>
                    <input
                      aria-label={`Média 30 dias da linha ${index + 1}`}
                      value={row.avgOrders30d}
                      inputMode="numeric"
                      disabled={disabled}
                      onChange={(e) =>
                        updateRow(index, { avgOrders30d: e.target.value })
                      }
                      style={{ width: "100%", textAlign: "right" }}
                    />
                    <CellError message={rowErrors.avgOrders30d} />
                  </td>
                  <td style={{ padding: "0.3rem 0.5rem", verticalAlign: "top", textAlign: "right" }}>
                    <button
                      type="button"
                      aria-label={`Remover SKU da linha ${index + 1}`}
                      disabled={disabled}
                      onClick={() => removeRow(index)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--text-muted)",
                        cursor: "pointer",
                        fontSize: 16,
                        padding: "0.2rem 0.35rem"
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function CellError({ message }: { message: string | undefined }): ReactNode {
  if (message === undefined) {
    return null;
  }
  return (
    <span role="alert" style={{ fontSize: 11, color: "var(--orange)", fontWeight: 600 }}>
      {message}
    </span>
  );
}
